import React, { useState, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Progress } from './ui/progress';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import {
  CloudUpload,
  Trash2,
  CheckCircle,
  XCircle,
  FileText
} from 'lucide-react';
import fileService from '../services/fileService';

const FileUploadComponent = ({
  entityType,
  entityId,
  onUploadComplete,
  onUploadError,
  multiple = true,
  maxFiles = 10,
  maxSizeMB = 50,
  allowedTypes = [],
  showPreview = true,
  disabled = false,
  className = '',
}) => {
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [successCount, setSuccessCount] = useState(0);
  const [detailsDialog, setDetailsDialog] = useState({ open: false, file: null });

  // Handle file selection
  const handleFileSelect = useCallback((selectedFiles) => {
    const newFiles = Array.from(selectedFiles).map(file => ({
      id: Date.now() + Math.random(),
      file,
      status: 'pending',
      progress: 0,
      error: null,
      description: '',
      tags: [],
    }));

    // Validate files
    const validatedFiles = newFiles.map(fileItem => {
      const validation = fileService.validateFile(fileItem.file, entityType, maxSizeMB);
      if (!validation.isValid) {
        return {
          ...fileItem,
          status: 'error',
          error: validation.errors.join(', ')
        };
      }
      return fileItem;
    });

    // Check file count limit
    const currentValidFiles = files.filter(f => f.status !== 'error').length;
    const newValidFiles = validatedFiles.filter(f => f.status !== 'error').length;

    if (currentValidFiles + newValidFiles > maxFiles) {
      setErrors(prev => [...prev, `最多只能上传 ${maxFiles} 个文件`]);
      return;
    }

    setFiles(prev => [...prev, ...validatedFiles]);
    setErrors([]);
  }, [entityType, maxSizeMB, maxFiles, files]);

  // Handle drag and drop
  const handleDrop = useCallback((event) => {
    event.preventDefault();
    setDragOver(false);

    if (disabled) return;

    const droppedFiles = Array.from(event.dataTransfer.files);
    handleFileSelect(droppedFiles);
  }, [disabled, handleFileSelect]);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    if (!disabled) setDragOver(true);
  }, [disabled]);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    setDragOver(false);
  }, []);

  // Handle file input change
  const handleFileInputChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    handleFileSelect(selectedFiles);
    event.target.value = '';
  };

  // Remove file from list
  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  // Upload all pending files
  const uploadFiles = async () => {
    const pendingFiles = files.filter(f => f.status === 'pending');
    if (pendingFiles.length === 0) return;

    setUploading(true);
    let successfulUploads = 0;

    for (const fileItem of pendingFiles) {
      try {
        // Update file status to uploading
        setFiles(prev => prev.map(f =>
          f.id === fileItem.id ? { ...f, status: 'uploading', progress: 0 } : f
        ));

        // Upload file
        const result = await fileService.uploadFile(
          fileItem.file,
          entityType,
          entityId,
          {
            description: fileItem.description,
            tags: fileItem.tags,
            onUploadProgress: (progressEvent) => {
              const progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setFiles(prev => prev.map(f =>
                f.id === fileItem.id ? { ...f, progress } : f
              ));
            }
          }
        );

        // Update file status to success
        setFiles(prev => prev.map(f =>
          f.id === fileItem.id
            ? { ...f, status: 'success', progress: 100, uploadResult: result }
            : f
        ));

        successfulUploads++;

        if (onUploadComplete) {
          onUploadComplete(result, fileItem.file);
        }

      } catch (error) {
        // Update file status to error
        setFiles(prev => prev.map(f =>
          f.id === fileItem.id
            ? { ...f, status: 'error', error: error.response?.data?.error || error.message }
            : f
        ));

        if (onUploadError) {
          onUploadError(error, fileItem.file);
        }
      }
    }

    setUploading(false);
    setSuccessCount(successfulUploads);

    // Auto-remove successful files after delay
    setTimeout(() => {
      setFiles(prev => prev.filter(f => f.status !== 'success'));
    }, 3000);
  };

  // Open file details dialog
  const openDetailsDialog = (fileItem) => {
    setDetailsDialog({ open: true, file: fileItem });
  };

  // Update file details
  const updateFileDetails = (description, tags) => {
    const fileId = detailsDialog.file.id;
    setFiles(prev => prev.map(f =>
      f.id === fileId ? { ...f, description, tags } : f
    ));
    setDetailsDialog({ open: false, file: null });
  };

  // Get file status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-success" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-destructive" />;
      default:
        return <FileText className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const pendingFiles = files.filter(f => f.status === 'pending');
  const hasFiles = files.length > 0;
  const canUpload = pendingFiles.length > 0 && !uploading && entityType && entityId;

  return (
    <div className={`w-full ${className}`}>
      {/* Upload Drop Zone */}
      <Card
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
          dragOver
            ? 'border-blue-500 bg-primary/10'
            : disabled
              ? 'border-gray-200 bg-muted cursor-not-allowed opacity-60'
              : 'border-gray-300 bg-white hover:bg-muted hover:border-blue-400 cursor-pointer'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={disabled ? undefined : () => document.getElementById('file-upload-input').click()}
      >
        <CloudUpload className={`h-12 w-12 mx-auto mb-4 ${dragOver ? 'text-primary' : 'text-muted-foreground'}`} />
        <h3 className="text-lg font-semibold mb-1">
          {disabled ? '文件上传已禁用' : '拖拽文件到此处或点击选择'}
        </h3>
        <p className="text-sm text-muted-foreground mb-1">
          {multiple ? `支持选择多个文件，最多 ${maxFiles} 个` : '只能选择一个文件'}
        </p>
        <p className="text-xs text-muted-foreground">
          最大文件大小: {maxSizeMB}MB
          {allowedTypes.length > 0 && ` | 支持格式: ${allowedTypes.join(', ')}`}
        </p>

        <input
          id="file-upload-input"
          data-testid="file-upload-input"
          type="file"
          multiple={multiple}
          accept={allowedTypes.length > 0 ? allowedTypes.map(t => `.${t}`).join(',') : '*'}
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled}
        />
      </Card>

      {/* Error Messages */}
      {errors.length > 0 && (
        <Alert variant="destructive" className="mt-4">
          <AlertDescription>
            {errors.map((error, index) => (
              <p key={index}>{error}</p>
            ))}
          </AlertDescription>
        </Alert>
      )}

      {/* Success Message */}
      {successCount > 0 && (
        <Alert className="mt-4 border-green-500 bg-success/10">
          <AlertDescription>
            已成功上传 {successCount} 个文件
          </AlertDescription>
        </Alert>
      )}

      {/* File List */}
      {hasFiles && showPreview && (
        <div className="mt-6">
          <h4 className="text-sm font-semibold mb-3">
            待上传文件 ({files.length})
          </h4>

          <div className="border rounded-lg divide-y">
            {files.map((fileItem) => (
              <div key={fileItem.id} className="flex items-center p-3">
                <div className="mr-3">
                  {getStatusIcon(fileItem.status)}
                </div>

                <div
                  className={`flex-1 min-w-0 ${fileItem.status === 'pending' ? 'cursor-pointer' : ''}`}
                  onClick={() => fileItem.status === 'pending' && openDetailsDialog(fileItem)}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm truncate">
                      {fileItem.file.name}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      ({fileService.formatFileSize(fileItem.file.size)})
                    </span>
                    {fileItem.tags.length > 0 && (
                      <div className="flex gap-1">
                        {fileItem.tags.slice(0, 2).map((tag, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                        {fileItem.tags.length > 2 && (
                          <Badge variant="secondary" className="text-xs">
                            +{fileItem.tags.length - 2}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    {fileItem.description && (
                      <p className="text-xs text-muted-foreground">
                        {fileItem.description}
                      </p>
                    )}
                    {fileItem.status === 'uploading' && (
                      <Progress
                        value={fileItem.progress}
                        className="mt-2 h-1"
                      />
                    )}
                    {fileItem.error && (
                      <p className="text-xs text-destructive">
                        {fileItem.error}
                      </p>
                    )}
                  </div>
                </div>

                {fileItem.status === 'pending' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 ml-2"
                    onClick={() => removeFile(fileItem.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Upload Button */}
          {canUpload && (
            <div className="mt-4 text-center">
              <Button
                onClick={uploadFiles}
                disabled={uploading}
              >
                <CloudUpload className="h-4 w-4 mr-2" />
                {uploading ? '上传中...' : `上传 ${pendingFiles.length} 个文件`}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* File Details Dialog */}
      <FileDetailsDialog
        open={detailsDialog.open}
        file={detailsDialog.file}
        onClose={() => setDetailsDialog({ open: false, file: null })}
        onSave={updateFileDetails}
      />
    </div>
  );
};

// File Details Dialog Component
const FileDetailsDialog = ({ open, file, onClose, onSave }) => {
  const [description, setDescription] = useState('');
  const [tagsString, setTagsString] = useState('');

  React.useEffect(() => {
    if (file) {
      setDescription(file.description || '');
      setTagsString(file.tags ? file.tags.join(', ') : '');
    }
  }, [file]);

  const handleSave = () => {
    const tags = tagsString.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
    onSave(description, tags);
  };

  if (!file) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>文件详细信息</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <p className="text-sm font-medium">文件名: {file.file.name}</p>
            <p className="text-sm text-muted-foreground">
              大小: {fileService.formatFileSize(file.file.size)}
            </p>
          </div>

          <div>
            <Label htmlFor="description">描述</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="为此文件添加描述..."
              rows={3}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="tags">标签</Label>
            <Input
              id="tags"
              value={tagsString}
              onChange={(e) => setTagsString(e.target.value)}
              placeholder="用逗号分隔多个标签"
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              例如: 重要, 合同, 技术文档
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>取消</Button>
          <Button onClick={handleSave}>保存</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FileUploadComponent;
